// LUMINA · components/DailyEnergyWidget.jsx
// Phase 58: 澄光日课 — Daily energy card, reusable (full + compact modes)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import EnergyRingChart from './EnergyRingChart';
import EnergyTierGate from './EnergyTierGate';
import { API } from '../constants/config';

const GD = "#D4AF37";
const BG = "#FDFBF7";
const TXT = "rgba(0,0,0,0.82)";
const TXT2 = "rgba(0,0,0,0.45)";

const LABEL_COLOR = {
  "气运亨通": "#064E3B",
  "平稳中正": "#D4AF37",
  "含藏蓄势": "#A07828",
  "静养蓄势": "rgba(0,0,0,0.35)",
};

// Energy archetype by day-pillar element
const ELEMENT_ARCHETYPE = {
  "金": { icon: "⚔️", name: "金曜日", trait: "果断 · 边界感强", tip: "适合做重要决定、梳理边界" },
  "木": { icon: "🌱", name: "木曜日", trait: "生长 · 创造力", tip: "适合开启新项目、学习新技能" },
  "水": { icon: "💧", name: "水曜日", trait: "流动 · 直觉敏锐", tip: "适合深度思考、灵活应变" },
  "火": { icon: "🔥", name: "火曜日", trait: "热情 · 行动力", tip: "适合社交表达、推进事项" },
  "土": { icon: "🏔️", name: "土曜日", trait: "稳定 · 承载力", tip: "适合夯实基础、整理内务" },
};

// Fallback by shishen group
const SHISHEN_ARCHETYPE = {
  "食神": { icon: "🎨", name: "灵感日", trait: "创造力满格" },
  "伤官": { icon: "⚡", name: "突破日", trait: "打破常规" },
  "正官": { icon: "⚖️", name: "秩序日", trait: "按规矩来" },
  "七杀": { icon: "🛡️", name: "承压日", trait: "迎难而上" },
  "正财": { icon: "💰", name: "务实日", trait: "稳扎稳打" },
  "偏财": { icon: "🎯", name: "机变日", trait: "灵活把握" },
  "正印": { icon: "📚", name: "内省日", trait: "安静学习" },
  "偏印": { icon: "🔮", name: "直觉日", trait: "相信直觉" },
  "比肩": { icon: "🤝", name: "协作日", trait: "找帮手" },
  "劫财": { icon: "🌊", name: "竞合日", trait: "竞争与协作并存" },
};

// Plain-language gloss for shishen types
const SHISHEN_PLAIN = {
  "食神": "灵感充沛，宜创作表达",
  "伤官": "才华外溢，宜突破创新",
  "正官": "责任加身，宜规范决策",
  "七杀": "压力磨砺，宜坚定意志",
  "正财": "务实财运，宜长线规划",
  "偏财": "财路灵活，宜把握商机",
  "正印": "智慧护持，宜学习内省",
  "偏印": "直觉敏锐，宜独处研究",
  "比肩": "人缘活跃，宜合作共事",
  "劫财": "人缘与竞争并存",
};

export default function DailyEnergyWidget({
  compact = false,
  birthData = null,  // { year, month, day, hour, gender }
  token = null,
  onNavigateDashboard,
  qimenData = null,  // Qimen daily data for arbiter (Phase 89)
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(!compact);
  const cardRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  // Share card as image
  const handleShare = useCallback(async () => {
    if (!cardRef.current || sharing) return;
    setSharing(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#FDFBF7',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      canvas.toBlob(blob => {
        if (!blob) { setSharing(false); return; }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const label = data?.energy_label || '能量';
        a.download = `LUMINA-${label}-${new Date().toISOString().slice(0,10)}.png`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        setSharing(false);
      }, 'image/png');
    } catch (e) {
      console.warn('Share failed:', e);
      alert('分享失败，请稍后再试');
      setSharing(false);
    }
  }, [sharing, data]);

  useEffect(() => {
    if (!birthData || !token) return;
    setLoading(true);
    fetch(`${API}/audit/daily-energy`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({
        birth_year: birthData.year, birth_month: birthData.month,
        birth_day: birthData.day,   birth_hour: birthData.hour,
        gender: birthData.gender || "male",
      }),
    })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d)))
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.detail || "无法加载日课"); setLoading(false); });
  }, [token, birthData?.year, birthData?.month, birthData?.day]);

  const labelColor = data ? (LABEL_COLOR[data.energy_label] || GD) : GD;

  // Compute energy archetype from day pillar
  const dayGan = data?.day_pillar?.ganzhi?.[0]; // first char of 干支
  const ganElement = { "甲":"木","乙":"木","丙":"火","丁":"火","戊":"土","己":"土","庚":"金","辛":"金","壬":"水","癸":"水" }[dayGan];
  const archetype = ELEMENT_ARCHETYPE[ganElement] || SHISHEN_ARCHETYPE[data?.shishen_type] || null;

  // ── Compact mode (for VajraDashboard) ─────────────────────────────────────
  if (compact) {
    if (loading) return (
      <div style={{ padding: "8px 0", fontSize: 12, color: TXT2, letterSpacing: 1 }}>日课加载中…</div>
    );
    if (error || !data) return null;
    return (
      <div onClick={onNavigateDashboard} style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 16px", borderRadius: 10,
        background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.2)",
        cursor: onNavigateDashboard ? "pointer" : "default",
        marginBottom: 12,
      }}>
        <EnergyRingChart score={data.energy_score} size={48} strokeWidth={5}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: GD, letterSpacing: 2, textTransform: "uppercase" }}>今日澄光日课</div>
          <div style={{ fontSize: 14, color: labelColor, fontWeight: 600, letterSpacing: 1 }}>
            {data.energy_label}
          </div>
          <div style={{ fontSize: 11, color: TXT2, letterSpacing: .5 }}>
            {data.keywords?.slice(0, 3).join(" · ")}
          </div>
        </div>
        {onNavigateDashboard && (
          <div style={{ fontSize: 11, color: TXT2 }}>→</div>
        )}
      </div>
    );
  }

  // ── Full mode ──────────────────────────────────────────────────────────────
  if (!token || !birthData) {
    return (
      <EnergyTierGate daysLeft={null} onUpgrade={() => window.location.href = "/pricing"} />
    );
  }

  if (loading) return (
    <div style={{
      background: "#fff", border: `1px solid rgba(212,175,55,0.28)`,
      borderRadius: 16, padding: 32, textAlign: "center",
      color: TXT2, fontSize: 13, letterSpacing: 1,
    }}>
      正在推演今日能量…
    </div>
  );

  if (error === "澄光日课需要订阅 $99/月，或开启7天免费试用") {
    return <EnergyTierGate daysLeft={null} onUpgrade={() => window.location.href = "/pricing"} />;
  }

  if (error) return (
    <div style={{
      padding: 24, textAlign: "center",
      background: "rgba(0,0,0,0.02)", borderRadius: 14,
      border: "1px dashed rgba(0,0,0,0.08)",
    }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>🔋</div>
      <div style={{ fontSize: 13, color: TXT2, letterSpacing: 1 }}>
        日课数据暂未就绪
      </div>
      <div style={{ fontSize: 11, color: "rgba(0,0,0,0.25)", marginTop: 4 }}>
        完成一次能量审计后自动开启
      </div>
    </div>
  );

  if (!data) return null;

  const today = new Date().toLocaleDateString("zh-CN", { month: "long", day: "numeric", weekday: "short" });

  return (
    <div ref={cardRef} style={{
      background: "#fff", border: "1px solid rgba(212,175,55,0.12)",
      borderRadius: 18, padding: "28px 28px 24px",
      boxShadow: "0 4px 20px rgba(132,107,50,0.04)",
    }}>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 10, color: GD, letterSpacing: 3, textTransform: "uppercase", marginBottom: 4 }}>
            🔋 澄光日课 · {today}
          </div>
          <div style={{ fontSize: 9, color: "rgba(0,0,0,0.25)", letterSpacing: 0.5, marginBottom: 6 }}>
            America/Toronto (EDT) · 真太阳时校验
          </div>
          <div style={{ fontSize: 22, color: labelColor, fontWeight: 700, letterSpacing: 1,
            fontFamily: "'Source Han Sans SC','Noto Sans CJK SC','Noto Serif SC',sans-serif" }}>
            {data.energy_label}
            {qimenData?.is_fu_yin && (
              <span style={{ fontSize: 13, color: "rgba(0,0,0,0.35)", fontWeight: 400, marginLeft: 8,
                letterSpacing: 0.5 }}>· 盘呈伏吟</span>
            )}
          </div>
          {archetype && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <span style={{ fontSize: 20 }}>{archetype.icon}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(0,0,0,0.75)', letterSpacing: 0.5 }}>
                {archetype.name}
              </span>
              <span style={{ fontSize: 12, color: TXT2, letterSpacing: 0.3 }}>
                {archetype.trait}
              </span>
              {archetype.tip && (
                <span style={{ fontSize: 11, color: 'rgba(6,78,59,0.6)', background: 'rgba(6,78,59,0.05)', padding: '2px 10px', borderRadius: 10 }}>
                  💡 {archetype.tip}
                </span>
              )}
            </div>
          )}
          {/* Score breakdown — subtle, not a math formula */}
          {(data.xi_ji_score != null && data.chong_he_score != null && data.shishen_score != null) && (
            <div style={{
              display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap",
              fontSize: 10, color: "rgba(0,0,0,0.35)", letterSpacing: 0.3,
            }}>
              <span>喜忌 {Math.round(data.xi_ji_score)}</span>
              <span>·</span>
              <span>刑冲 {Math.round(data.chong_he_score)}</span>
              <span>·</span>
              <span>十神 {Math.round(data.shishen_score)}</span>
              {data.context_modifier && data.context_modifier !== 1.0 && (
                <span style={{ color: "rgba(212,175,55,0.4)" }}>
                  · 运调 {data.context_modifier.toFixed(2)}
                </span>
              )}
            </div>
          )}
          {data.trial_days_remaining !== undefined && (
            <div style={{ fontSize: 10, color: "rgba(0,0,0,0.35)", marginTop: 4, letterSpacing: 1 }}>
              体验剩余 {data.trial_days_remaining} 天
            </div>
          )}
        </div>
        <EnergyRingChart score={data.energy_score} size={96} strokeWidth={8}/>
      </div>

      {/* Day pillar + shishen */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
        <span style={{
          fontSize: 12, padding: "4px 12px", borderRadius: 12, letterSpacing: 1.5,
          background: "rgba(212,175,55,0.08)", color: GD,
          border: "1px solid rgba(212,175,55,0.2)", lineHeight: "20px",
        }}>日柱 {data.day_pillar?.ganzhi}</span>
        <span style={{
          fontSize: 12, padding: "4px 12px", borderRadius: 12, letterSpacing: 1,
          background: "rgba(0,0,0,0.04)", color: TXT2,
          border: "1px solid rgba(0,0,0,0.08)", lineHeight: "20px",
        }}>{data.shishen_type}日</span>
        {SHISHEN_PLAIN[data.shishen_type] && (
          <span style={{
            fontSize: 11, color: TXT2, lineHeight: "28px", letterSpacing: 0.3,
            whiteSpace: "nowrap",
          }}>{SHISHEN_PLAIN[data.shishen_type]}</span>
        )}
        {data.keywords?.map(k => (
          <span key={k} style={{
            fontSize: 12, padding: "4px 12px", borderRadius: 12, letterSpacing: 1,
            background: "rgba(139,105,20,0.05)", color: "rgba(0,0,0,0.55)", lineHeight: "20px",
          }}>{k}</span>
        ))}
      </div>

      {/* Narrative */}
      <div style={{
        fontSize: 14, color: TXT, lineHeight: 2.1, letterSpacing: .3,
        marginBottom: data.warnings?.length || data.vajra_bridge ? 16 : 0,
      }}>
        {data.narrative_zh}
      </div>

      {/* Warnings */}
      {data.warnings?.length > 0 && (
        <div style={{
          background: "rgba(180,83,9,0.04)", border: "1px solid rgba(180,83,9,0.12)",
          borderRadius: 8, padding: "10px 14px", marginBottom: 14,
          fontSize: 12, color: "#92400e", lineHeight: 1.7,
        }}>
          {data.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}

      {/* Arbiter: Qimen fu_yin hedge (Phase 89) */}
      {qimenData?.is_fu_yin && (
        <div style={{
          background: "rgba(6,78,59,0.04)", border: "1px solid rgba(6,78,59,0.14)",
          borderRadius: 8, padding: "10px 14px", marginBottom: 14,
          fontSize: 12, color: "#064E3B", lineHeight: 1.7,
        }}>
          ⚠ 今日奇门盘呈伏吟（星门多伏本位），整体宜守不宜攻。虽八字气运尚可，建议在稳健中暗自推进，忌大张旗鼓。
        </div>
      )}

      {/* Vajra bridge */}
      {data.vajra_bridge && (
        <div style={{
          background: "rgba(212,175,55,0.06)",
          border: "1px solid rgba(212,175,55,0.22)",
          borderRadius: 10, padding: "12px 16px",
          fontSize: 12, color: "rgba(0,0,0,0.62)", lineHeight: 1.7,
        }}>
          <span style={{ fontSize: 10, color: GD, letterSpacing: 2, display: "block", marginBottom: 4 }}>
            ☸ 修行建议
          </span>
          {data.vajra_bridge.practice_hint}
        </div>
      )}

      {/* Share button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <button
          onClick={handleShare}
          disabled={sharing}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 16px', borderRadius: 20,
            border: '1px solid rgba(212,175,55,0.3)',
            background: sharing ? 'rgba(212,175,55,0.06)' : 'transparent',
            color: GD, fontSize: 12, cursor: sharing ? 'wait' : 'pointer',
            letterSpacing: 0.5, transition: 'all .2s',
          }}
          onMouseEnter={e => { if (!sharing) { e.target.style.background = 'rgba(212,175,55,0.08)'; }}}
          onMouseLeave={e => { if (!sharing) { e.target.style.background = 'transparent'; }}}
        >
          {sharing ? '生成中…' : '📤 分享今日能量'}
        </button>
      </div>
    </div>
  );
}
